/**
 * WorkspaceUser — v1.8. Chat-only user-facing workspace.
 *
 * Reuses the SAME backend that /workspace/:id uses (postWorkspaceMessage,
 * getWorkspaceConversation, publish, etc.). No new endpoint, no new
 * engine — just a clean surface tailored for end users:
 *   - Header: N-avatar, name (editable), ID + status, credits, avatar
 *   - Chat: user + assistant bubbles, compact delivery card
 *   - Composer at bottom (attach, voice, send)
 *
 * Advanced tools (drawer, publish rollback, ver JSON, engine timeline)
 * remain in /workspace/:id for admins.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Paperclip, Mic, Send, MicOff, CheckCircle2, Eye, Loader2, Pencil, ExternalLink, X, PlayCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';
import { CreditsButton } from '../components/credits/CreditsButton';
import { PublishProgressModal, PublishedResult } from '../components/chat/PublishProgressModal';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../contexts/LocaleContext';
import { useCredits } from '../contexts/CreditsContext';
import { toast } from 'sonner';

interface Turn {
  id: string;
  role: 'user' | 'assistant';
  kind: string;
  content: string;
  ts?: string;
  delivery?: {
    project_name?: string;
    preview_url?: string | null;
    preview_ready?: boolean;
    files?: number;
    title?: string;
    summary?: string;
    whats_done?: string[];
    whats_pending?: string[];
    recommendations?: string[];
    why_pending?: string;
  } | null;
}

// Normalise the WorkspaceConversation payload into simple turns.
// v1.30 fix: preserve `kind` so the filter can distinguish Architect
// messages (kind='message', role='architect') from Builder internal
// bitácora / state_change turns. The previous implementation stripped
// `kind`, causing isUserFacingTurn to drop EVERY assistant turn.
function normaliseTurns(conv: any): Turn[] {
  if (!conv) return [];
  const raw = conv.turns || conv.messages || [];
  return raw.map((t: any, i: number): Turn => {
    // Backend uses role='user' | 'engine' | 'system' | 'architect'.
    // For the chat surface we collapse everything non-user into 'assistant'.
    const rawRole = t.role || (t.author === 'user' ? 'user' : 'assistant');
    const role: 'user' | 'assistant' = rawRole === 'user' ? 'user' : 'assistant';
    const kind = t.kind || 'message';
    const content = t.content || t.text || t.message || '';
    const isDeliveryCard = kind === 'delivery_card';
    const detail = t.detail || {};
    const legacyDelivery = Boolean(t.delivery) || Boolean(t.result?.project_name) || t.kind === 'delivery';
    let delivery: Turn['delivery'] = null;
    if (isDeliveryCard) {
      delivery = {
        title: detail.title || 'Tu proyecto está listo',
        summary: detail.summary || content,
        whats_done: detail.whats_done || [],
        whats_pending: detail.whats_pending || [],
        recommendations: detail.recommendations || [],
        preview_url: detail.preview_url || detail.public_url || null,
        preview_ready: detail.preview_ready !== false && Boolean(detail.preview_url || detail.public_url),
      };
    } else if (legacyDelivery) {
      delivery = {
        project_name: t.delivery?.project_name || t.result?.project_name || undefined,
        preview_url: t.delivery?.preview_url || t.result?.preview_url || null,
        files: t.delivery?.files || t.result?.files || 0,
      };
    }
    return {
      id: t.id || `turn-${i}`,
      role,
      kind,
      content,
      ts: t.created_at || t.ts || undefined,
      delivery,
      // Preserve original role for the filter (architect vs engine)
      _origRole: t.role,
      // Sprint v1.37 · propagate detail.ephemeral so the historial
      // reload can filter out narration cards ("🧠 Entendiendo…",
      // "🧱 Construyendo…") that only made sense in the live cycle.
      _ephemeral: Boolean(detail.ephemeral),
    } as Turn & { _origRole?: string; _ephemeral?: boolean };
  }).filter((t: Turn) => t.content || t.delivery);
}

// ── Sprint v1.37 · filtro DEFINITIVO del historial persistido.
// El bug reportado por el usuario: al reabrir un workspace aparecen
// múltiples cards de "Entendiendo tu proyecto…", "Construyendo…",
// "Preparando…" que ya no deberían mostrarse. Root cause: la narración
// del Architect se persiste en la DB (role='architect', kind='message')
// y al recargar se re-renderiza como si fueran mensajes nuevos.
//
// Solución: cuando ya hay una Delivery Card definitiva para un
// ciclo (bloque entre dos mensajes del usuario), descartamos toda
// la narración efímera de ese bloque — solo conservamos el mensaje
// original del usuario y la Delivery Card final.
function pruneHistoricalEphemera(turns: Turn[]): Turn[] {
  // Compute delivery-card presence per block.
  const blocks: Array<{ start: number; end: number; hasCard: boolean }> = [];
  let blockStart = -1;
  turns.forEach((t, i) => {
    if (t.role === 'user') {
      if (blockStart >= 0) blocks.push({ start: blockStart, end: i - 1, hasCard: false });
      blockStart = i;
    }
  });
  if (blockStart >= 0) blocks.push({ start: blockStart, end: turns.length - 1, hasCard: false });
  blocks.forEach((b) => {
    for (let i = b.start; i <= b.end; i++) {
      if (turns[i].kind === 'delivery_card' || turns[i].delivery) { b.hasCard = true; break; }
    }
  });
  const dropIdx = new Set<number>();
  blocks.forEach((b) => {
    if (!b.hasCard) return;  // active/ongoing block — keep everything
    for (let i = b.start; i <= b.end; i++) {
      const t = turns[i] as Turn & { _ephemeral?: boolean };
      if (t._ephemeral) { dropIdx.add(i); continue; }
      // Legacy narration (pre v1.37, no ephemeral flag) — recognise
      // by the well-known emoji prefixes used by _narrate().
      if (t.role === 'assistant' && t.kind === 'message') {
        const c = (t.content || '').trim();
        if (/^(🧠|🧱|🧪|📦|🔁|🔧|✅|📥|🔍|🛠|💾|🎨|🏗)/.test(c)) {
          dropIdx.add(i);
        }
      }
    }
  });
  return turns.filter((_t, i) => !dropIdx.has(i));
}

const fmtTime = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export default function WorkspaceUser() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const nav = useNavigate();
  // Sprint P0 Quirúrgico Feb 2026 · alias 'tr' para no colisionar con
  // la variable de turno `t` dentro del .map de renderizado.
  const tr = useT();
  // Sprint Estabilización Final · Feb 2026 · fast-poll de saldo
  // mientras el motor está construyendo. Sin esto, el sobregiro -7
  // se refleja solo tras 15s (siguiente tick del CreditsContext), y
  // el usuario percibe que "salir y volver a entrar" es lo único que
  // dispara la pausa.
  const { activateFastPoll, refresh: refreshCredits } = useCredits();
  const { user: authUser } = useAuth();
  const [me, setMe] = useState<any>(null);
  const [workspace, setWorkspace] = useState<any>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [engineActive, setEngineActive] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const recogRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [listening, setListening] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Sprint UX Workspace v1.35 · Publish progress modal.
  const [publishOpen, setPublishOpen] = useState(false);
  // Sprint P0 Quirúrgico Feb 2026 · Rollback confirm modal state.
  // Los callbacks (doRollback) se declaran más abajo, después de
  // loadWorkspace (temporal dead zone).
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [rollbackInFlight, setRollbackInFlight] = useState(false);

  const loadWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [ws, conv] = await Promise.all([
        apiService.getWorkspace(workspaceId),
        apiService.getWorkspaceConversation(workspaceId).catch(() => null),
      ]);
      setWorkspace(ws);
      setNameDraft(ws?.name || '');
      setTurns(pruneHistoricalEphemera(normaliseTurns(conv)));
      // Sprint v1.42 · P0 fix — backend stores `engine_running` /
      // `engine_waiting` / `engine_deploying`. The old comparison
      // against 'active'/'running' NEVER matched, so the composer
      // lock relied purely on the optimistic setEngineActive(true)
      // in send() — which got wiped on the very next 2s poll.
      const st = String(conv?.engine_state || '');
      setEngineActive(
        st === 'engine_running'
        || st === 'engine_waiting'
        || st === 'engine_deploying'
        || st === 'running'
        || st === 'active'
      );
    } catch (err: any) {
      toast.error(tr('workspaceUser.loadError'));
    }
  }, [workspaceId]);

  useEffect(() => { apiService.getMe().then(setMe).catch(() => setMe(null)); }, []);
  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  // Sprint P0 Preview Roto (Feb 2026) · Auto-wake al abrir el workspace.
  // Antes solo despertábamos el runtime cuando el usuario hacía click
  // en Preview. Ahora lo hacemos SILENCIOSAMENTE al montar el workspace
  // para que cuando el usuario navegue o pulse Preview, el runtime ya
  // esté disponible sin latencia extra. Fire-and-forget, sin bloqueo.
  useEffect(() => {
    if (!workspaceId) return;
    // Slight delay so we don't compete with the initial workspace fetch
    // for network/CPU on the container.
    const t = setTimeout(() => {
      void apiService.runtimeWake(workspaceId).catch(() => { /* silent */ });
    }, 500);
    return () => clearTimeout(t);
  }, [workspaceId]);

  // Sprint P0.2 v1.46 · PREVIEW PRE-WARM AGRESIVO — apenas detectamos
  // que el workspace puede tener scaffold, disparamos:
  //   1) Un GET al runtime proxy con ?nexus_warmup=1 para spawn del
  //      runtime en background (crea la cookie nx_preview con el
  //      token para que los assets carguen sin auth extra).
  //   2) Un probe ligero cada 4s mientras la pestaña está viva para
  //      confirmar que el HTML está listo cuando el usuario haga tap.
  // Cuando el usuario haga click en Preview, la primera petición ya
  // devuelve HTML de forma inmediata porque el runtime está caliente
  // y el disco ya tiene el index.html premium generado.
  const runtimeWarmedRef = React.useRef<string | null>(null);
  // Sprint P0.3 v1.47 · PREVIEW READY TRACKING.
  // Poll ligero al endpoint /nexus_probe=1 (200+X-Nexus-Ready:1 cuando
  // el HTML está en disco). Se detiene automáticamente al recibir OK.
  // openPreview() consulta este ref para decidir si abre la tab de
  // inmediato o si difiere la apertura hasta que el probe confirme.
  const previewReadyRef = React.useRef<boolean>(false);
  useEffect(() => {
    if (!workspaceId || !workspace) return;
    const hasScaffold = Boolean(
      workspace?.config?.custom_config?.imported_path
      || workspace?.scaffold_root
      || workspace?.status === 'active'
      || workspace?.status === 'ready'
    );
    if (!hasScaffold) return;
    if (runtimeWarmedRef.current === workspaceId) return;
    runtimeWarmedRef.current = workspaceId;
    previewReadyRef.current = false;
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const token = localStorage.getItem('nexus_token') || '';
    // Warm-up con token: crea cookie nx_preview + arranca runtime.
    void fetch(`${backend}/api/workspaces/${workspaceId}/runtime/proxy/?token=${encodeURIComponent(token)}&nexus_warmup=1`, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
    }).catch(() => { runtimeWarmedRef.current = null; });

    // Probe polling: cada 1.5s hasta confirmar. Se cancela al desmontar.
    // Sprint P0.8 Fase 1 v1.51 · LOOP BREAKER: máximo 40 iteraciones
    // (~60s) SIN respuesta OK. Después mostramos error al usuario y
    // paramos el poll. Antes podía correr indefinidamente si el
    // runtime nunca respondía → consumo innecesario + ilusión de
    // "cargando eterno". Ahora nos rendimos con un mensaje claro.
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40;
    const probeBase = `${backend}/api/workspaces/${workspaceId}/runtime/proxy/`;
    const poll = async () => {
      if (cancelled || previewReadyRef.current) return;
      if (attempts >= MAX_ATTEMPTS) {
        // Silencioso: no forzamos un toast en el poll de mount (solo cuando el usuario hace click).
        return;
      }
      attempts++;
      try {
        const r = await fetch(`${probeBase}?nexus_probe=1&_=${Date.now()}`, {
          cache: 'no-store', credentials: 'omit',
        });
        if (r.headers.get('X-Nexus-Ready') === '1') {
          previewReadyRef.current = true;
          return;
        }
      } catch { /* keep polling */ }
      if (!cancelled) setTimeout(poll, 1500);
    };
    void poll();
    return () => { cancelled = true; };
  }, [workspaceId, workspace]);

  // Poll while the engine is running (2s interval; stops on inactive).
  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    const tick = async () => {
      try {
        const conv = await apiService.getWorkspaceConversation(workspaceId);
        if (!alive) return;
        const dbTurns = pruneHistoricalEphemera(normaliseTurns(conv));
        // Sprint Final · Preservar turnos locales optimistas hasta que
        // el backend los persista. Si un turno local (id que empieza
        // con "local-") todavía no tiene un turno equivalente en la
        // respuesta del backend (mismo role=user + mismo contenido
        // trimmed), lo conservamos al final del array. Así el mensaje
        // del usuario NUNCA desaparece del chat.
        setTurns((prevLocal) => {
          const locals = prevLocal.filter((t) => t.id.startsWith('local-'));
          if (locals.length === 0) return dbTurns;
          // Count how many user-turns with each content the DB already
          // has, then keep any local optimistic turn beyond that count.
          // This handles duplicate messages (e.g. two "sí" in a row).
          const dbCounts = new Map<string, number>();
          for (const t of dbTurns) {
            if (t.role !== 'user') continue;
            const k = (t.content || '').trim();
            dbCounts.set(k, (dbCounts.get(k) || 0) + 1);
          }
          const pending: Turn[] = [];
          for (const l of locals) {
            const k = (l.content || '').trim();
            const remaining = dbCounts.get(k) || 0;
            if (remaining > 0) {
              dbCounts.set(k, remaining - 1);
            } else {
              pending.push(l);
            }
          }
          return pending.length ? [...dbTurns, ...pending] : dbTurns;
        });
        const st = String(conv?.engine_state || '');
        const active = (
          st === 'engine_running'
          || st === 'engine_waiting'
          || st === 'engine_deploying'
          || st === 'running'
          || st === 'active'
        );
        setEngineActive(active);
      } catch { /* silent */ }
    };
    const int = setInterval(tick, engineActive ? 2000 : 8000);
    return () => { alive = false; clearInterval(int); };
  }, [workspaceId, engineActive]);

  // Sprint Final · Auto-scroll inteligente.
  //   • Si el usuario está cerca del fondo (< 100 px), auto-scroll
  //     suave hacia abajo cuando llegue un mensaje nuevo.
  //   • Si el usuario subió a leer mensajes antiguos, NO forzamos el
  //     scroll; mostramos un pill "Nuevos mensajes ↓" para volver.
  const [autoStick, setAutoStick] = useState(true);
  const [pendingNew, setPendingNew] = useState(false);
  const prevTurnsLen = useRef(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      const nearBottom = distanceFromBottom < 100;
      setAutoStick(nearBottom);
      if (nearBottom) setPendingNew(false);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); };
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const growth = turns.length - prevTurnsLen.current;
    prevTurnsLen.current = turns.length;
    if (growth <= 0) return;
    if (autoStick) {
      // Smooth if the user is following along; jump on initial load.
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      setPendingNew(true);
    }
  }, [turns.length, autoStick]);
  const scrollToBottomNow = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setPendingNew(false);
    setAutoStick(true);
  };

  // 2026-Q1 · Self-dev workspace actions.
  const publishSelfDevSandbox = useCallback(async () => {
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('nexus_token') || '';
      const r = await fetch(`${backend}/api/self/publish-sandbox`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (r.ok) {
        toast.success(
          j?.delivered
            ? tr('workspaceUser.publishSandboxOk')
            : tr('workspaceUser.publishSandboxRegistered'),
        );
      } else {
        toast.error(j?.detail || tr('workspaceUser.publishSandboxFail'));
      }
    } catch {
      toast.error(tr('workspaceUser.publishSandboxFail'));
    }
  }, [tr]);

  const promoteSelfDevProduction = useCallback(async () => {
    const confirmed = window.confirm(tr('workspaceUser.promoteConfirm'));
    if (!confirmed) return;
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('nexus_token') || '';
      const r = await fetch(`${backend}/api/self/promote-production`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (r.ok) {
        toast.success(
          j?.delivered
            ? tr('workspaceUser.promoteProdOk')
            : tr('workspaceUser.promoteProdRegistered'),
        );
      } else {
        toast.error(j?.detail || tr('workspaceUser.promoteProdFail'));
      }
    } catch {
      toast.error(tr('workspaceUser.promoteProdFail'));
    }
  }, [tr]);

  // Sprint P0 Quirúrgico Feb 2026 · Rollback handler.
  const doRollback = useCallback(async () => {
    if (!workspaceId || rollbackInFlight) return;
    setRollbackInFlight(true);
    try {
      const r = await apiService.previewRollback(workspaceId);
      if (r?.restored) {
        toast.success(tr('rollback.success'));
        // No generamos versión nueva ni consumimos créditos. Solo recargamos
        // la conversación para que el Delivery Card refleje el estado.
        await loadWorkspace();
      } else {
        toast.error(tr('rollback.error'));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      if (msg && String(msg).toLowerCase().includes('no earlier version')) {
        toast.error(tr('rollback.empty'));
      } else {
        toast.error(tr('rollback.error'));
      }
    } finally {
      setRollbackInFlight(false);
      setRollbackConfirmOpen(false);
    }
  }, [workspaceId, rollbackInFlight, tr, loadWorkspace]);

  const send = async () => {
    // v1.42 · Ref-based fallback: si el state React está desincronizado
    // del DOM (paste, autofill, o algún test framework), leemos también
    // del input real. `text` puede estar vacío pero el input DOM tener
    // contenido — el usuario NO debe ver un click ignorado silenciosamente.
    const domVal = (inputRef.current?.value || '').trim();
    const stateVal = text.trim();
    const msg = stateVal || domVal;
    if (!msg || !workspaceId || sending) return;
    setSending(true);
    setText('');
    if (inputRef.current) inputRef.current.value = '';
    // v1.31 · Give the user IMMEDIATE feedback (bug: "parece que nunca
    // fue enviado"): optimistic user bubble + engineActive flip BEFORE
    // the network call so the persistent motor indicator lights up at
    // the moment of confirmation.
    setEngineActive(true);
    setTurns((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'user',
        kind: 'message',
        content: msg,
        ts: new Date().toISOString(),
      },
      {
        id: `local-engine-${Date.now()}`,
        role: 'assistant',
        kind: 'bitacora',
        content: '🔧 Preparando el Builder…',
        ts: new Date().toISOString(),
      } as any,
    ]);
    try {
      // Sprint v1.42 · P0 fix — latch engineActive locally when the
      // Architect confirms trigger_builder=true. This is a
      // belt-and-suspenders in case the backend engine_state poll
      // races (the state might briefly be idle between the return
      // of architect_chat and the moment _dispatch_builder_guarded
      // flips it to engine_running). Without this latch the composer
      // could un-lock for a fraction of a second which is enough for
      // a user on mobile to think the app is frozen.
      const r: any = await apiService.architectChat({
        message: msg,
        workspace_id: workspaceId,
      });
      if (r?.trigger_builder) {
        setEngineActive(true);
      }
      await loadWorkspace();
    } catch (err: any) {
      setEngineActive(false);
      toast.error(err?.response?.data?.detail || 'No se pudo enviar el mensaje.');
    } finally { setSending(false); }
  };

  const toggleListening = () => {
    // Web Speech API — same dedup fix as Home.tsx. Snapshot the
    // baseline text and always REPLACE with baseline+transcript so
    // the interim/final callbacks never duplicate words.
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { toast.error('Tu navegador no soporta reconocimiento de voz.'); return; }
    if (listening) {
      recogRef.current?.stop?.();
      setListening(false);
      return;
    }
    const baseline = text.trim();
    const r = new SR();
    r.lang = 'es-ES';
    r.interimResults = true;
    r.onresult = (e: any) => {
      const full = Array.from(e.results).map((res: any) => res[0].transcript).join(' ').trim();
      setText(baseline ? `${baseline} ${full}` : full);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.start();
    recogRef.current = r;
    setListening(true);
  };

  const saveName = async () => {
    if (!workspaceId || !nameDraft.trim() || nameDraft === workspace?.name) { setEditingName(false); return; }
    try {
      await apiService.updateWorkspace(workspaceId, { name: nameDraft.trim() });
      setWorkspace((w: any) => ({ ...w, name: nameDraft.trim() }));
      toast.success(tr('workspaceUser.nameUpdated'));
    } catch { toast.error(tr('workspaceUser.renameFail')); }
    setEditingName(false);
  };

  const attach = () => fileRef.current?.click();
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !workspaceId) return;
    setAttaching(true);
    try {
      await apiService.uploadWorkspaceAttachment(workspaceId, file);
      toast.success(`${tr('workspaceUser.attach')}: ${file.name}`);
    } catch { toast.error(tr('workspaceUser.attachFail')); }
    finally { setAttaching(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const openPreview = useCallback(() => {
    if (!workspaceId) return;
    // Sprint P0 Cierre · Super-persistencia UX. Antes de abrir el
    // Preview siempre lanzamos runtime/wake en paralelo. Es idempotente:
    // no-op si el runtime ya está corriendo, spawn silencioso si acaba
    // de expirar naturalmente, spawn tras deploy-restart si Mongo aún
    // no ha completado la restauración. Fire-and-forget: no bloqueamos
    // el click del usuario.
    void apiService.runtimeWake(workspaceId).catch(() => { /* silent */ });
    // Sprint Final Cierre · El Preview vive mientras exista el workspace.
    // SIEMPRE intentamos abrir el runtime proxy interno primero — sirve
    // el scaffold en disco y no depende de la publicación. Solo si el
    // workspace tiene un `public_url` de despliegue, ese tiene
    // prioridad porque es la versión pública final.
    const publicUrl = workspace?.public_url || workspace?.published_url;
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('nexus_token') || '';
      if (publicUrl) {
        window.open(publicUrl, '_blank', 'noopener');
        return;
      }
      if (backend) {
        const base = `${backend}/api/workspaces/${workspaceId}/runtime/proxy/`;
        // Sprint P0.6 v1.49 · PREVIEW = VISITANTE POR DEFECTO.
        // Antes abríamos con `nexus_edit=1` y el modo edición aparecía
        // al instante. El usuario pidió explícitamente: "la página
        // siempre debe abrir como la verá cualquier visitante. Dentro
        // del menú debe existir una opción 'Modo edición'." Ahora la
        // pestaña Preview abre limpia (visitor view) — el toggle de
        // edición se activa desde el header del workspace.
        const buildUrl = (t: string) => t
          ? `${base}?token=${encodeURIComponent(t)}`
          : `${base}`;
        if (previewReadyRef.current) {
          window.open(buildUrl(token), '_blank', 'noopener');
          return;
        }
        // Deferred open flow. NO tab hasta que el probe responda listo.
        const dismiss = toast.loading(tr('workspaceUser.previewPreparing'), { duration: 15000 });
        let tries = 0;
        const probeUrl = `${base}?nexus_probe=1&_=${Date.now()}`;
        const tick = async () => {
          tries++;
          try {
            const r = await fetch(probeUrl, { cache: 'no-store', credentials: 'omit' });
            if (r.headers.get('X-Nexus-Ready') === '1') {
              previewReadyRef.current = true;
              toast.dismiss(dismiss);
              window.open(buildUrl(token), '_blank', 'noopener');
              return;
            }
          } catch { /* keep polling */ }
          if (tries < 40) {
            setTimeout(tick, 400);
          } else {
            toast.dismiss(dismiss);
            toast.error('El Preview aún no está listo. Vuelve a intentarlo en unos segundos.');
          }
        };
        void tick();
        return;
      }
    } catch { /* fall through */ }
    toast.error('No se pudo abrir el Preview.');
  }, [workspace, workspaceId]);

  // Sprint P0.6 v1.49 · MODO EDICIÓN EXPLÍCITO.
  // El usuario pidió que la página SIEMPRE abra como la verá un
  // visitante y que exista un botón claro "Modo edición" para abrir la
  // vista con las herramientas del constructor. Este handler abre una
  // nueva pestaña con `nexus_edit=1` — sólo dentro del Preview
  // (sandbox); la URL publicada nunca lleva este flag.
  const openEditMode = useCallback(() => {
    if (!workspaceId) return;
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('nexus_token') || '';
      if (!backend) { toast.error('No se pudo abrir el modo edición.'); return; }
      const base = `${backend}/api/workspaces/${workspaceId}/runtime/proxy/`;
      const url = token
        ? `${base}?token=${encodeURIComponent(token)}&nexus_edit=1`
        : `${base}?nexus_edit=1`;
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('No se pudo abrir el modo edición.');
    }
  }, [workspaceId]);

  const openDeliveryPreview = useCallback((card: Turn['delivery']) => {
    // Sprint P0 Cierre · Auto-wake antes de abrir la Delivery Preview.
    if (workspaceId) {
      void apiService.runtimeWake(workspaceId).catch(() => { /* silent */ });
    }
    // Sprint v1.42.5 · P0 fix — el navegador BLOQUEA `window.open()`
    // si no ocurre en el mismo tick síncrono del click. Antes hacíamos
    // `await refreshToken()` antes de abrir, y el popup blocker mataba
    // el primer tap (sensación de "no funciona, hay que tocar 2-3
    // veces"). Ahora abrimos la tab INMEDIATAMENTE con el token en
    // memoria y refrescamos el token en background (silenciosamente)
    // para futuros clicks.
    const token = localStorage.getItem('nexus_token') || '';
    let target = '';
    if (card?.preview_url) {
      try {
        const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        target = card.preview_url.startsWith('http')
          ? card.preview_url
          : `${backend}${card.preview_url}`;
        if (token && !target.includes('token=')) {
          target += (target.includes('?') ? '&' : '?') + `token=${encodeURIComponent(token)}`;
        }
        // Sprint P0.6 v1.49 · Delivery-card Preview también abre en
        // modo visitante por defecto (sin `nexus_edit`). El modo
        // edición se activa desde el botón dedicado del workspace.
        // (En pasadas anteriores forzábamos nexus_edit=1 aquí.)
      } catch { target = ''; }
    }

    if (target) {
      // Open FIRST — sync with the click event so popup blockers stay
      // out of the way.
      window.open(target, '_blank', 'noopener');
    } else {
      openPreview();
    }

    // Background: refresh the token so the NEXT click carries a fresh
    // one. We don't await this and we don't touch the current tab.
    void apiService.refreshToken().catch(() => { /* noop */ });
  }, [openPreview]);

  const wsStatus = workspace?.status === 'active' ? { label: tr('workspaceUser.statusActive'), color: 'hsl(150 65% 55%)' } : { label: workspace?.status || tr('workspaceUser.statusLoading'), color: 'hsl(var(--text-muted))' };

  // Sprint Final Cierre · Detección robusta del estado "construyendo".
  //   El indicador vivo NO puede desaparecer antes de que aparezca la
  //   Delivery Card. Bandera activa cuando:
  //   1) el engine reporta activo, o
  //   2) hay una respuesta del arquitecto que confirma inicio (build)
  //      o hay turnos del Builder (role=engine / kind=bitacora /
  //      kind=state_change) posteriores al último mensaje del usuario,
  //      y aún no llegó la Delivery Card.
  const buildActivityAfterLastUser = useMemo(() => {
    let lastUser = -1;
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === 'user') { lastUser = i; break; }
    }
    if (lastUser < 0) return { hasCard: true, hasBuildSignal: false };
    let hasCard = false;
    let hasBuildSignal = false;
    for (let i = lastUser + 1; i < turns.length; i++) {
      const t = turns[i];
      if (t.kind === 'delivery_card') hasCard = true;
      if (t.kind === 'bitacora' || t.kind === 'state_change') hasBuildSignal = true;
      // Sprint v1.42 · Fix — ephemeral architect narrations (🧠 Analizando…,
      // ✏️ Aplicando la modificación…, 🧱 Construyendo…, 🧪 Verificando…,
      // 📦 Preparando entrega, ✅ Cambio aplicado) count as build signals.
      // Antes: si el Architect emitía SÓLO efímeros (sin bitácora), el
      // composer se reactivaba en <1s tras el envío del usuario y
      // dejaba enviar otra edición encima del pipeline en curso.
      // Detectamos por marcador emoji al inicio del contenido, ya que
      // el backend a veces no propaga detail.ephemeral en el turn.
      const isNarration = (
        (t as any)._ephemeral === true
        || /^\s*(🧠|✏️|🧱|🧪|📦|✅|⚙️)/u.test(t.content || '')
      );
      if (
        t.role === 'assistant'
        && t.kind !== 'delivery_card'
        && isNarration
      ) {
        hasBuildSignal = true;
      }
    }
    return { hasCard, hasBuildSignal };
  }, [turns]);
  const hasDeliveryCardAfterLastUser = buildActivityAfterLastUser.hasCard;
  const isBuilding = !hasDeliveryCardAfterLastUser && (
    engineActive
    || sending
    || buildActivityAfterLastUser.hasBuildSignal
    || (turns.length > 0 && turns[turns.length - 1]?.role === 'user')
  );

  // Sprint Estabilización Final · Feb 2026 · while a build is in
  // flight, ask CreditsContext to poll every 2s instead of 15s. When
  // the LLM burn reaches the -7 overdraft floor and the backend
  // emits the paused Delivery Card, the chip flips to red / -N in
  // real time — no need to close and re-open the app.
  useEffect(() => {
    if (isBuilding) {
      activateFastPoll(180);
      void refreshCredits({ force: true });
    }
  }, [isBuilding, activateFastPoll, refreshCredits]);

  // Sprint P0.7 v1.50 · MOBILE VIEWPORT + KEYBOARD-AWARE LAYOUT.
  // El usuario reportó (Safari iPhone / Chrome Android) que después
  // de escribir en el chat la vista quedaba "colgada": scroll roto,
  // huecos vacíos, contenido cortado. Root cause: usábamos `h-screen`
  // (100vh) que Safari interpreta como la altura del viewport CON
  // chrome, sin ajustarse cuando aparece el teclado. Además el chat
  // no manejaba `visualViewport.resize` para restaurar el scroll y
  // no aplicaba `env(safe-area-inset)`.
  //
  // Fix:
  //   1) Usar `100dvh` (`viewport-fit=cover` de PWA) que sí se ajusta
  //      al chrome dinámico del navegador.
  //   2) Escuchar `window.visualViewport.resize` — cuando el teclado
  //      aparece / desaparece, restauramos `scrollTop` del composer y
  //      ajustamos el offset del contenedor del chat.
  //   3) Aplicar `env(safe-area-inset-bottom)` al composer para que
  //      no quede debajo de la home-bar de iPhone.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const kb = Math.max(0, window.innerHeight - vv.height);
        document.documentElement.style.setProperty('--nx-keyboard', kb + 'px');
        // Scroll last message into view when keyboard opens.
        if (kb > 0) {
          try {
            const scroller = document.querySelector('[data-nexus-chat-scroller]') as HTMLElement | null;
            if (scroller) scroller.scrollTop = scroller.scrollHeight;
          } catch { /* noop */ }
        }
      });
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="bg-slate-950 nx-circuit text-slate-100 flex flex-col"
      style={{
        height: '100dvh',
        minHeight: '100dvh',
        paddingBottom: 'calc(var(--nx-keyboard, 0px) + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="workspace-user-page"
    >
      {/* Custom header (matches Reference Image screen 7) */}
      <header
        className="sticky top-0 z-20 backdrop-blur-md shrink-0"
        style={{
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          {/* Sprint Corrección Final #7 · botón explícito Volver al Home. */}
          <button
            onClick={() => nav('/home')}
            className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg text-xs font-medium shrink-0"
            style={{ background: 'hsl(var(--bg) / 0.6)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
            data-testid="wsu-back-btn"
            aria-label={tr('workspaceUser.backHome')}
            title={tr('workspaceUser.backHome')}
          >
            <ArrowLeft size={14} /> <span className="hidden sm:inline">{tr('common.back')}</span>
          </button>
          <button
            onClick={() => nav('/home')}
            className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
            style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.35)', color: 'hsl(var(--accent))' }}
            data-testid="wsu-nexus-icon"
            aria-label={tr('sidebar.home')}
          >
            <span className="text-sm font-bold" style={{ fontFamily: 'monospace' }}>N</span>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {editingName ? (
                <input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') { setEditingName(false); setNameDraft(workspace?.name || ''); } }}
                  autoFocus
                  className="bg-transparent outline-none border-b text-sm font-medium min-w-0 flex-1"
                  style={{ borderColor: 'hsl(var(--accent) / 0.5)', color: 'hsl(var(--text))' }}
                  data-testid="wsu-name-input"
                />
              ) : (
                <>
                  <div className="text-sm font-medium truncate" data-testid="wsu-name">
                    {workspace?.name || tr('workspaceUser.workspaceFallback')}
                  </div>
                  <button
                    onClick={() => setEditingName(true)}
                    className="opacity-60 hover:opacity-100"
                    aria-label={tr('workspaceUser.rename')}
                    data-testid="wsu-name-edit"
                  >
                    <Pencil size={12} style={{ color: 'hsl(var(--text-muted))' }} />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-[11px] mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
              <span className="font-mono" data-testid="wsu-id">ID: {workspaceId?.slice(0, 12)}</span>
              <span className="inline-flex items-center gap-1" style={{ color: wsStatus.color }} data-testid="wsu-status">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: wsStatus.color }} />
                {wsStatus.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(workspace?.public_url || workspace?.published_url || workspace) && (
              <button
                onClick={openPreview}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: 'hsl(var(--bg-elev) / 0.6)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
                data-testid="wsu-preview-btn"
              >
                <Eye size={12} /> {tr('common.preview')}
              </button>
            )}
            {workspace && (
              <button
                onClick={async () => {
                  try {
                    const r = await apiService.runtimeWake(workspaceId!);
                    if (r?.woke) toast.success(tr('profile.previewAwake'));
                    else if (r?.reason === 'already_running') toast.success(tr('profile.previewAwake'));
                  } catch (e: any) {
                    const msg = e?.response?.data?.detail || tr('profile.wakeFailed');
                    toast.error(msg);
                  }
                }}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition"
                style={{ background: 'hsl(var(--bg-elev) / 0.6)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
                title={tr('profile.wakePreviewHint')}
                data-testid="wsu-wake-preview-btn"
              >
                <PlayCircle size={12} /> {tr('profile.wakePreview')}
              </button>
            )}
            {workspace && (
              <button
                onClick={openEditMode}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{
                  background: 'hsl(var(--accent) / 0.12)',
                  border: '1px solid hsl(var(--accent) / 0.35)',
                  color: 'hsl(var(--accent))',
                }}
                data-testid="wsu-edit-mode-btn"
                title={tr('workspaceUser.editModeHint')}
              >
                <Pencil size={12} /> {tr('workspaceUser.editMode')}
              </button>
            )}
            <CreditsButton
              testId="wsu-credits-chip"
              returnTo={typeof window !== 'undefined' ? window.location.pathname : undefined}
            />
            <AdminAvatarMenu
              isAdmin={authUser?.role === 'admin'}
              initials={(authUser?.name?.[0] || authUser?.email?.[0] || 'N').toUpperCase()}
            />
          </div>
        </div>
      </header>

      {/* Chat area */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto relative" data-testid="wsu-chat-scroll">
        <AnimatePresence>
          {pendingNew && !autoStick && (
            <motion.button
              key="new-messages-pill"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={scrollToBottomNow}
              className="fixed left-1/2 -translate-x-1/2 bottom-24 z-10 px-4 py-2 rounded-full text-xs font-medium shadow-lg backdrop-blur-md flex items-center gap-1.5"
              style={{
                background: 'hsl(var(--accent))',
                color: 'hsl(210 40% 8%)',
                boxShadow: '0 10px 30px hsl(var(--accent) / 0.35)',
              }}
              data-testid="wsu-new-messages-pill"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              {tr('workspaceUser.newMessages')}
              <span className="ml-0.5">↓</span>
            </motion.button>
          )}
        </AnimatePresence>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {turns.length === 0 && !engineActive && (
            <div className="text-center py-12" style={{ color: 'hsl(var(--text-muted))' }}>
              <div className="text-sm">{tr('workspaceUser.startPrompt')}</div>
            </div>
          )}

          <AnimatePresence initial={false}>
            {(() => {
              // Sprint UX Workspace (Feb 2026) · Delivery Card inmutable.
              // El usuario reportó que la card "cambia sola" después de
              // entregarse: el pipeline emite múltiples turns con
              // kind='delivery_card' (uno inicial del arquitecto + otro
              // enriquecido con árbitros). Aquí conservamos SOLO la
              // primera por bloque de conversación (por bloque = tras
              // el último mensaje del usuario). Cualquier delivery_card
              // adicional emitida sobre el mismo bloque se descarta.
              const seenCardBlocks: Set<number> = new Set();
              let blockIdx = 0;
              const visible: Turn[] = [];
              for (const t of turns.filter(isUserFacingTurn)) {
                if (t.role === 'user') { blockIdx += 1; }
                if (t.delivery || (t as any).kind === 'delivery_card') {
                  if (seenCardBlocks.has(blockIdx)) continue;
                  seenCardBlocks.add(blockIdx);
                }
                visible.push(t);
              }
              return visible;
            })()
              .map((t, i) => (
              <motion.div
                key={t.id + i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={t.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                data-testid={`wsu-turn-${t.role}-${i}`}
              >
                <div className={`max-w-[85%] flex gap-2 ${t.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div
                    className="w-7 h-7 rounded-full grid place-items-center shrink-0 mt-0.5"
                    style={t.role === 'user'
                      ? { background: 'hsl(var(--bg-elev))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }
                      : { background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.35)', color: 'hsl(var(--accent))' }
                    }
                  >
                    <span className="text-[10px] font-bold" style={{ fontFamily: 'monospace' }}>
                      {t.role === 'user' ? '👤' : 'N'}
                    </span>
                  </div>
                  <div>
                    {!t.delivery && (
                      <div
                        className="rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
                        style={t.role === 'user'
                          ? { background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.32)', color: 'hsl(var(--text))' }
                          : { background: 'hsl(var(--bg-elev) / 0.75)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }
                        }
                      >
                        {t.content}
                      </div>
                    )}
                    {t.delivery && (
                      <div
                        className="mt-2 rounded-2xl p-4 flex flex-col gap-3"
                        style={{
                          background: 'linear-gradient(150deg, hsl(var(--accent) / 0.14), hsl(var(--accent) / 0.03))',
                          border: '1px solid hsl(var(--accent) / 0.35)',
                          boxShadow: '0 20px 60px hsl(var(--accent) / 0.12)',
                        }}
                        data-testid="wsu-delivery-card"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle2 size={24} style={{ color: 'hsl(var(--accent))', filter: 'drop-shadow(0 0 10px hsl(var(--accent) / 0.6))' }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold" data-testid="wsu-delivery-title">
                              {t.delivery.title || t.delivery.project_name || tr('workspaceUser.projectReady')}
                            </div>
                            <div className="text-[11px]" style={{ color: 'hsl(var(--text-muted))' }}>
                              {tr('workspaceUser.deliveryReady')}
                            </div>
                          </div>
                        </div>
                        {t.delivery.summary && (
                          <div className="text-xs leading-relaxed" style={{ color: 'hsl(var(--text))' }}>
                            {t.delivery.summary}
                          </div>
                        )}
                        {Array.isArray(t.delivery.whats_done) && t.delivery.whats_done.length > 0 && (
                          <ul className="text-xs space-y-1 pl-4" style={{ color: 'hsl(var(--text-muted))', listStyleType: 'disc' }} data-testid="wsu-delivery-done">
                            {t.delivery.whats_done.slice(0, 5).map((w: string, k: number) => (
                              <li key={k}>{w}</li>
                            ))}
                          </ul>
                        )}
                        {Array.isArray(t.delivery.whats_pending) && t.delivery.whats_pending.length > 0 && (
                          <div className="text-xs" style={{ color: 'hsl(38 92% 62%)' }} data-testid="wsu-delivery-pending">
                            <div className="text-[10px] uppercase tracking-widest mb-1 opacity-80">{tr('workspaceUser.pending')}</div>
                            <ul className="space-y-1 pl-4" style={{ listStyleType: 'disc' }}>
                              {t.delivery.whats_pending.slice(0, 3).map((w: string, k: number) => (
                                <li key={k}>{w}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {Array.isArray(t.delivery.recommendations) && t.delivery.recommendations.length > 0 && (
                          <div className="text-xs" style={{ color: 'hsl(210 90% 68%)' }} data-testid="wsu-delivery-recs">
                            <div className="text-[10px] uppercase tracking-widest mb-1 opacity-80">{tr('workspaceUser.recommendations')}</div>
                            <ul className="space-y-1 pl-4" style={{ listStyleType: 'disc' }}>
                              {t.delivery.recommendations.slice(0, 3).map((w: string, k: number) => (
                                <li key={k}>{w}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap" data-testid="wsu-delivery-actions">
                          <button
                            onClick={() => navigator.clipboard?.writeText(t.delivery?.summary || t.content)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium"
                            style={{
                              background: 'transparent',
                              border: '1px solid hsl(var(--border))',
                              color: 'hsl(var(--text-muted))',
                            }}
                            data-testid="wsu-delivery-copy"
                          >
                            {tr('workspaceUser.copy')}
                          </button>
                          {/* Sprint UX Workspace v1.35 · ListenButton lee TODA la card:
                              resumen + cambios + pendientes + recomendaciones. */}
                          <ListenButton
                            text={[
                              t.delivery.summary || t.content,
                              Array.isArray(t.delivery.whats_done) && t.delivery.whats_done.length > 0
                                ? tr('workspaceUser.changesDone') + (t.delivery.whats_done as string[]).join('. ') + '.'
                                : '',
                              Array.isArray(t.delivery.whats_pending) && t.delivery.whats_pending.length > 0
                                ? tr('workspaceUser.pendingLabel') + (t.delivery.whats_pending as string[]).join('. ') + '.'
                                : '',
                              Array.isArray(t.delivery.recommendations) && t.delivery.recommendations.length > 0
                                ? tr('workspaceUser.recommendationsLabel') + (t.delivery.recommendations as string[]).join('. ') + '.'
                                : '',
                            ].filter(Boolean).join(' ')}
                            testId={`wsu-delivery-listen-${t.id}`}
                          />
                          {/* Sprint P0 Quirúrgico Feb 2026 · Rollback button.
                              Integra con Copy/Listen/Preview. Abre confirmación
                              antes de restaurar la versión anterior. */}
                          <button
                            onClick={() => setRollbackConfirmOpen(true)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium"
                            style={{
                              background: 'transparent',
                              border: '1px solid hsl(var(--border))',
                              color: 'hsl(var(--text-muted))',
                              touchAction: 'manipulation',
                            }}
                            data-testid="wsu-delivery-rollback"
                            aria-label={tr('workspaceUser.rollback')}
                            title={tr('workspaceUser.rollback')}
                          >
                            <ArrowLeft size={11} /> {tr('workspaceUser.rollback')}
                          </button>
                          <button
                            onClick={(e) => {
                              // Sprint v1.35 · guarantee first-click open on
                              // touch devices: preventDefault stops the click
                              // from getting swallowed by any ancestor with
                              // its own tap handler; stopPropagation prevents
                              // parent focus/scroll interceptors from eating
                              // the event before we navigate.
                              e.preventDefault();
                              e.stopPropagation();
                              openDeliveryPreview(t.delivery);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer select-none"
                            style={{
                              background: 'hsl(var(--bg-elev) / 0.6)',
                              border: '1px solid hsl(var(--accent) / 0.35)',
                              color: 'hsl(var(--text))',
                              touchAction: 'manipulation',   // <- iOS: skip 300ms delay
                            }}
                            data-testid="wsu-delivery-preview"
                          >
                            <Eye size={11} /> {tr('workspaceUser.preview')}
                          </button>
                          <button
                            onClick={() => {
                              if (workspace?.is_self_dev) {
                                publishSelfDevSandbox();
                              } else {
                                setPublishOpen(true);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ml-auto"
                            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)', touchAction: 'manipulation' }}
                            data-testid="wsu-delivery-publish"
                          >
                            {workspace?.is_self_dev ? tr('workspaceUser.publishSandbox') : tr('workspaceUser.publish')} <ExternalLink size={11} />
                          </button>
                          {workspace?.is_self_dev && (
                            <button
                              onClick={promoteSelfDevProduction}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                              style={{
                                background: 'transparent',
                                border: '1px solid hsl(var(--accent) / 0.55)',
                                color: 'hsl(var(--accent))',
                              }}
                              data-testid="wsu-selfdev-promote-prod"
                            >
                              {tr('workspaceUser.promoteProduction')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={`mt-1 text-[10px] flex items-center gap-2 ${t.role === 'user' ? 'text-right justify-end' : ''}`} style={{ color: 'hsl(var(--text-muted))' }}>
                      <span>{t.role === 'assistant' ? tr('workspaceUser.nexusAi') : ''}{fmtTime(t.ts)}</span>
                      {/* Sprint Cierre Final v1.27 · botón Escuchar en
                          cada respuesta del Arquitecto. */}
                      {t.role === 'assistant' && (
                        <ListenButton text={t.content} testId={`chat-listen-btn-${t.id}`} />
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {isBuilding && <RotatingStatusIndicator />}
        </div>
      </div>

      {/* v1.31 · Persistent Engine Running chip — the user demanded a
          permanent visible indicator so the app never feels frozen.
          Sits above the composer, disappears only when the Delivery
          Card arrives.
          v1.37 · Now includes a Cancel button so the user can stop the
          build at any time (returns credits, unlocks composer). */}
      {isBuilding && (
        <div
          className="flex justify-center"
          data-testid="wsu-engine-running-chip"
        >
          <div
            className="mb-2 inline-flex items-center gap-2 rounded-full pl-3 pr-1 py-1 text-[11px] font-semibold shadow-lg"
            style={{
              background: 'hsl(var(--accent) / 0.14)',
              color: 'hsl(var(--accent))',
              border: '1px solid hsl(var(--accent) / 0.4)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <motion.span
              animate={{ scale: [1, 1.25, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: 'hsl(var(--accent))', boxShadow: '0 0 8px hsl(var(--accent))' }}
            />
            {tr('workspaceUser.engineRunning')}
            <button
              onClick={() => {
                if (!window.confirm(tr('workspaceUser.buildCanceledDetail'))) return;
                setEngineActive(false);
                setSending(false);
                // Add a synthetic user-facing turn so the isBuilding
                // computation sees an ended block (delivery-like)
                // without polluting the server history — this is a
                // client-side abort. The server-side pipeline will
                // continue but the UI stops showing progress.
                setTurns((prev) => [...prev, {
                  id: `local-cancel-${Date.now()}`,
                  role: 'assistant',
                  kind: 'delivery_card',
                  content: tr('workspaceUser.buildCanceled'),
                  ts: new Date().toISOString(),
                  delivery: {
                    title: tr('workspaceUser.buildCanceled'),
                    summary: tr('workspaceUser.buildCanceledDetail'),
                    whats_done: [],
                    whats_pending: [],
                    recommendations: [],
                    preview_url: null,
                    preview_ready: false,
                  },
                } as any]);
                toast.success(tr('workspaceUser.buildCanceled'));
              }}
              className="ml-2 w-6 h-6 rounded-full grid place-items-center transition-colors"
              style={{
                background: 'hsl(var(--bg-elev))',
                color: 'hsl(var(--text-muted))',
              }}
              aria-label={tr('workspaceUser.cancelBuild')}
              data-testid="wsu-cancel-build-btn"
            >
              <X size={11} />
            </button>
          </div>
        </div>
      )}

      {/* Composer */}
      <div
        className="sticky bottom-0 shrink-0 backdrop-blur-md"
        style={{
          background: 'linear-gradient(0deg, hsl(var(--bg-elev) / 0.95), hsl(var(--bg-elev) / 0.5))',
          borderTop: '1px solid hsl(var(--border))',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={attach}
            disabled={attaching || isBuilding}
            className="w-10 h-10 rounded-full grid place-items-center shrink-0 disabled:opacity-50"
            style={{ background: 'hsl(var(--bg-elev))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
            aria-label={tr('workspaceUser.attach')}
            data-testid="wsu-attach-btn"
          >
            {attaching ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="wsu-file-input" />
          <button
            onClick={toggleListening}
            disabled={isBuilding}
            className="w-10 h-10 rounded-full grid place-items-center shrink-0 disabled:opacity-50"
            style={{
              background: listening ? 'hsl(var(--accent) / 0.15)' : 'hsl(var(--bg-elev))',
              border: `1px solid ${listening ? 'hsl(var(--accent) / 0.5)' : 'hsl(var(--border))'}`,
              color: listening ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))',
            }}
            aria-label={listening ? tr('workspaceUser.voiceStop') : tr('workspaceUser.voiceStart')}
            data-testid="wsu-voice-btn"
          >
            {listening ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
          <div
            className="flex-1 flex items-center rounded-full px-4 py-2 min-h-[40px]"
            style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))' }}
          >
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={isBuilding ? tr('workspaceUser.composerWorking') : tr('workspaceUser.composerType')}
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: 'hsl(var(--text))' }}
              data-testid="wsu-input"
              disabled={sending || isBuilding}
            />
          </div>
          <button
            onClick={send}
            disabled={sending || isBuilding}
            className="w-10 h-10 rounded-full grid place-items-center shrink-0 disabled:opacity-50"
            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
            aria-label={tr('workspaceUser.sendAria')}
            data-testid="wsu-send-btn"
          >
            {(sending || isBuilding) ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
      {/* Sprint UX Workspace v1.35 · Premium publish experience. */}
      <PublishProgressModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        workspaceName={workspace?.name}
        publishFn={async (): Promise<PublishedResult> => {
          if (!workspaceId) throw new Error('workspace not loaded');
          const r = await apiService.publishWorkspace(workspaceId, 'auto');
          const url = (r as any).url || (r as any).public_url || (r as any).deployment_url || '';
          if (!url) throw new Error('Publicación completada pero sin URL pública');
          // Sprint P0 v1.43 · Notifica al Home (y a cualquier otra
          // vista) que hay una nueva publicación → refresco automático
          // sin recargar. La miniatura se rellena unos segundos después
          // vía el segundo evento cuando Playwright termina.
          try {
            window.dispatchEvent(new CustomEvent('nexus:publish-completed', {
              detail: { workspace_id: workspaceId, url },
            }));
            // Give the thumbnail-capture background task a chance and
            // fire a follow-up refresh in ~30s.
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('nexus:publish-thumbnail-ready', {
                detail: { workspace_id: workspaceId },
              }));
            }, 30000);
          } catch { /* noop */ }
          return {
            url,
            thumbnail: (r as any).thumbnail_url || null,
            name: workspace?.name,
            published_at: (r as any).published_at || new Date().toISOString(),
          };
        }}
      />
      {/* Sprint P0 Quirúrgico Feb 2026 · Rollback confirmation modal.
          Se muestra ANTES de ejecutar el POST /api/workspaces/{id}/rollback.
          Créditos consumidos previamente NO se devuelven. */}
      {rollbackConfirmOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
          data-testid="wsu-rollback-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => { if (!rollbackInFlight) setRollbackConfirmOpen(false); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full max-w-md rounded-2xl p-5 space-y-4"
            style={{
              background: 'hsl(var(--bg-elev))',
              border: '1px solid hsl(var(--border))',
              boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold" style={{ color: 'hsl(var(--text))' }} data-testid="wsu-rollback-title">
              {tr('rollback.confirmTitle')}
            </div>
            <div className="text-sm leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }} data-testid="wsu-rollback-body">
              {tr('rollback.confirmBody')}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setRollbackConfirmOpen(false)}
                disabled={rollbackInFlight}
                className="px-4 py-2 rounded-full text-xs font-medium"
                style={{
                  background: 'transparent',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--text-muted))',
                  opacity: rollbackInFlight ? 0.5 : 1,
                }}
                data-testid="wsu-rollback-cancel"
              >
                {tr('rollback.cancel')}
              </button>
              <button
                onClick={doRollback}
                disabled={rollbackInFlight}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                style={{
                  background: 'hsl(var(--accent))',
                  color: 'hsl(210 40% 8%)',
                  opacity: rollbackInFlight ? 0.7 : 1,
                }}
                data-testid="wsu-rollback-confirm"
              >
                {rollbackInFlight
                  ? <><Loader2 size={12} className="animate-spin" /> {tr('rollback.inFlight')}</>
                  : tr('rollback.confirmCta')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ─── Sprint v1.30 · Filtro user-facing definitivo.
// Regla simple:
//   • SIEMPRE mostrar mensajes del usuario y del arquitecto.
//   • SIEMPRE mostrar la Delivery Card.
//   • OCULTAR turnos técnicos del Builder (role='engine' o
//     role='system' con kind bitacora/state_change/result/error).
//   • El filtro NO usa regex de jerga: si el Arquitecto habla en
//     lenguaje natural, siempre se muestra al usuario.

function isUserFacingTurn(t: any): boolean {
  if (!t) return false;
  const origRole = t._origRole || t.role;
  // User's own messages.
  if (origRole === 'user') return true;
  // Delivery Card siempre se renderiza.
  if (t.kind === 'delivery_card') return true;
  // Turnos del Arquitecto (asesor): siempre visibles.
  if (origRole === 'architect') {
    // Excepto los turnos internos del pipeline de árbitros que emitimos
    // como state_change para debugging (nunca deben verse en chat).
    if (t.kind === 'state_change' || t.kind === 'bitacora') return false;
    return true;
  }
  // Todo lo demás (engine bitácora/state_change/result/error/system)
  // queda oculto — son ruido técnico del Builder.
  return false;
}

// ─── RotatingStatusIndicator (Sprint Final Cierre)
// Un solo indicador vivo debajo del último mensaje. Rota entre estados
// cortos (Analizando → Diseñando → Construyendo → Compilando → Preview
// → Validando). Reemplaza al anterior en el mismo lugar — nunca se
// acumulan mensajes. Se oculta cuando termina el flujo.
const _STATUS_KEYS = [
  'workspaceUser.statusAnalyzing',
  'workspaceUser.statusDesigning',
  'workspaceUser.statusBuilding',
  'workspaceUser.statusIntegrating',
  'workspaceUser.statusTesting',
  'workspaceUser.statusCompiling',
  'workspaceUser.statusPreview',
  'workspaceUser.statusValidating',
];
const RotatingStatusIndicator: React.FC = () => {
  const tr = useT();
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    // Ritmo natural: 4-7 s entre estados.
    let cancelled = false;
    const tick = () => {
      const wait = 4000 + Math.random() * 3000;
      const id = window.setTimeout(() => {
        if (cancelled) return;
        setIdx((i) => (i + 1) % _STATUS_KEYS.length);
        tick();
      }, wait);
      return id;
    };
    tick();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="flex items-center gap-2 pl-9" data-testid="wsu-engine-active">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: 'hsl(var(--accent))' }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.3 }}
          className="text-xs"
          style={{ color: 'hsl(var(--text-muted))' }}
          data-testid={`wsu-status-${idx}`}
        >
          {tr(_STATUS_KEYS[idx])}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ─── ListenButton (Hallazgo #12 · Escuchar cualquier respuesta del Arquitecto)
// Sprint Final · caché in-memory (module level) para no re-sintetizar el
// mismo texto en cada click. Clave = SHA-corta del texto. LRU-cap para
// que sesiones largas no crezcan indefinidamente.
const _TTS_URL_CACHE = new Map<string, string>();
const _TTS_CACHE_MAX = 80;
const _ttsKey = (t: string) => {
  // Djb2 hash — barato y suficiente para deduplicar en cliente.
  let h = 5381;
  for (let i = 0; i < t.length; i++) h = ((h << 5) + h) + t.charCodeAt(i);
  return `tts_${(h >>> 0).toString(36)}_${t.length}`;
};
const _ttsCacheSet = (key: string, url: string) => {
  if (_TTS_URL_CACHE.has(key)) _TTS_URL_CACHE.delete(key); // refresh LRU position
  _TTS_URL_CACHE.set(key, url);
  while (_TTS_URL_CACHE.size > _TTS_CACHE_MAX) {
    const firstKey = _TTS_URL_CACHE.keys().next().value;
    if (firstKey === undefined) break;
    _TTS_URL_CACHE.delete(firstKey);
  }
};
const ListenButton: React.FC<{ text: string; testId?: string }> = ({ text, testId }) => {
  const tr = useT();
  const [playing, setPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const prefetchedRef = React.useRef(false);

  // Sprint Final Cierre · Prefetch en hover/focus para que el primer
  // click reproduzca instantáneamente. Si el usuario nunca hace hover
  // (mobile), el click igual funciona con el flujo normal.
  const prefetch = React.useCallback(() => {
    if (prefetchedRef.current) return;
    if (!text || !text.trim()) return;
    prefetchedRef.current = true;
    const key = _ttsKey(text);
    if (_TTS_URL_CACHE.has(key)) return;
    apiService.getTTSUrl(text)
      .then((url) => { if (url) _ttsCacheSet(key, url); })
      .catch(() => { prefetchedRef.current = false; });
  }, [text]);

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    if (!text || !text.trim()) return;
    try {
      setLoading(true);
      const key = _ttsKey(text);
      let url = _TTS_URL_CACHE.get(key);
      if (!url) {
        url = await apiService.getTTSUrl(text);
        if (url) _ttsCacheSet(key, url);
      } else {
        // Bump LRU position on hit.
        _ttsCacheSet(key, url);
      }
      if (!url) throw new Error('no url');
      const a = new Audio(url);
      // Sprint UX Workspace v1.34 · iOS/Android voice first-tap fix.
      // Same rationale as VoicePlayer.tsx: iOS Safari refuses to play
      // audio unless (1) `playsinline` is set and (2) `play()` is
      // called inside the same user-gesture stack. Since we might
      // have awaited the network on a cold cache, we catch
      // NotAllowedError explicitly and prompt the user to tap again.
      a.setAttribute('playsinline', 'true');
      a.setAttribute('webkit-playsinline', 'true');
      a.preload = 'auto';
      audioRef.current = a;
      a.onended = () => { setPlaying(false); audioRef.current = null; };
      a.onerror = () => { setPlaying(false); audioRef.current = null; };
      a.onplaying = () => { setPlaying(true); setLoading(false); };
      try {
        await a.play();
      } catch (playErr: any) {
        if (playErr?.name === 'NotAllowedError') {
          setLoading(false);
          audioRef.current = null;
          // The URL is cached now, so the next tap will play instantly.
          toast.error(tr('workspaceUser.ttsRetap'));
          return;
        }
        throw playErr;
      }
    } catch {
      setPlaying(false);
      setLoading(false);
      audioRef.current = null;
      toast.error(tr('workspaceUser.ttsError'));
    }
  };
  const icon = loading ? '⏳' : playing ? '⏸' : '🔊';
  const label = loading ? tr('workspaceUser.ttsPreparing') : playing ? tr('workspaceUser.ttsStop') : tr('workspaceUser.ttsListen');
  return (
    <button
      onClick={toggle}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onTouchStart={prefetch}
      disabled={loading}
      className="inline-flex items-center gap-1 opacity-70 hover:opacity-100 disabled:opacity-50"
      style={{ color: 'hsl(var(--accent))' }}
      title={label}
      aria-label={label}
      data-testid={testId || 'chat-listen-btn'}
    >
      {icon} <span>{label}</span>
    </button>
  );
};

// end of file
